import React, { useEffect, useState } from 'react';
import { API, authHeaders } from '../../utils/helpers';

// ── Parts to Order email (24 Oct) ────────────────────────────────────────────
//
// Once a day, at the time and on the days set here, the people picked here get
// the Parts to Order list by email, grouped by vendor with totals at cost
// (server/helpers/partsOrderEmail.js). Saved with the page's Save button, as
// settings.partsOrder: the settings object is the page's own (passed in), and
// the only state here is the staff list.
//
// People are added from a dropdown and shown as removable names, so a shop
// with many logins does not get a wall of checkboxes (Manny, 24 Oct).

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIMES = [];
for (let m = 6 * 60; m <= 20 * 60; m += 15) TIMES.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
const label12 = (t) => { const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };

export default function PartsOrderEmailSettings({ T, S, settings, setSettings, setSaved }) {
  const [users, setUsers] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/parts-order/email-candidates`, { credentials: 'include', headers: authHeaders() })
      .then(r => (r.ok ? r.json() : { users: [] }))
      .then(d => setUsers(Array.isArray(d.users) ? d.users : []))
      .catch(() => setUsers([]));
  }, []);
  const p = settings.partsOrder || {};
  const picked = (p.emailUserIds || []).map(Number);
  const time = p.emailTime || '14:00';
  const days = Array.isArray(p.emailDays) ? p.emailDays.map(Number) : [1, 2, 3, 4, 5];
  const set = (patch) => { setSettings({ ...settings, partsOrder: { emailUserIds: picked, emailTime: time, emailDays: days, ...patch } }); setSaved(false); };
  const nameOf = (id) => { const u = (users || []).find(x => Number(x.id) === id); return u ? u.name : `User ${id}`; };
  const emailOf = (id) => { const u = (users || []).find(x => Number(x.id) === id); return u ? u.email : ''; };
  const notPicked = (users || []).filter(u => !picked.includes(Number(u.id)));
  const field = { padding: '6px 8px', borderRadius: 6, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontSize: 13 };

  return (
    <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ ...S.settingsCardTitle, color: T.text }}>Parts to Order email</div>
      <div style={{ ...S.settingsCardDesc, color: T.textSub }}>
        Once a day, the people below get everything on Parts to Order, grouped by vendor with each vendor's total at cost, and every part
        number, SKU and UPC ready to copy into a distributor's order. Parts added since the last email are marked NEW. Set the time before
        your distributors' cut-off for next-day delivery. Nothing on the list, or nobody picked: no email.
      </div>

      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Send it to</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', flex: 1 }}>
          {picked.map(id => (
            <span key={id} title={emailOf(id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px', borderRadius: 14, fontSize: 13, color: T.text, background: T.input, border: `1px solid ${T.inputBorder}` }}>
              {nameOf(id)}
              <button onClick={() => set({ emailUserIds: picked.filter(x => x !== id) })} aria-label={`Remove ${nameOf(id)}`}
                style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
            </span>
          ))}
          {users === null ? <span style={{ fontSize: 12, color: T.textSub }}>Loading staff…</span>
            : notPicked.length > 0 && (
              <select value="" onChange={e => e.target.value && set({ emailUserIds: [...picked, Number(e.target.value)] })} style={field}>
                <option value="">{picked.length ? '+ Add someone' : 'Choose who gets it'}</option>
                {notPicked.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </select>
            )}
          {users && users.length === 0 && <span style={{ fontSize: 12, color: T.textSub }}>No staff with an email address.</span>}
        </div>
      </div>

      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Send it at</span>
        <select value={time} onChange={e => set({ emailTime: e.target.value })} style={field}>
          {TIMES.map(t => <option key={t} value={t}>{label12(t)}</option>)}
        </select>
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>shop time</span>
      </div>

      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>On these days</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DAYS.map((d, i) => {
            const on = days.includes(i);
            return (
              <button key={d} onClick={() => set({ emailDays: on ? days.filter(x => x !== i) : [...days, i].sort() })}
                style={{ padding: '5px 10px', borderRadius: 14, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                  background: on ? T.filterActive : T.filterInactive, color: on ? T.filterActiveText : T.filterInactiveText, border: `1px solid ${on ? T.filterActive : T.filterBorder}` }}>
                {d}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>
        A Manager or Admin can also send it any time from Parts to Order, with Email this list now.
      </div>
    </div>
  );
}
